import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { Centrifuge, Subscription, PublicationContext } from 'centrifuge';
import api from '../services/api';
import { useAuth } from './AuthContext';
import type { WebSocketEvent } from '../types';

type EventHandler = (event: WebSocketEvent) => void;

interface WebSocketContextType {
  isConnected: boolean;
  subscribe: (handler: EventHandler) => () => void;
  lastEvent: WebSocketEvent | null;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { isAuthenticated } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WebSocketEvent | null>(null);

  const centrifugeRef = useRef<Centrifuge | null>(null);
  const userSubRef = useRef<Subscription | null>(null);
  const systemSubRef = useRef<Subscription | null>(null);
  const handlersRef = useRef<Set<EventHandler>>(new Set());

  const subscribe = useCallback((handler: EventHandler) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const handlePublication = useCallback((ctx: PublicationContext) => {
    const event = ctx.data as WebSocketEvent;
    setLastEvent(event);
    handlersRef.current.forEach(handler => handler(event));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Disconnect if user logs out
      if (centrifugeRef.current) {
        centrifugeRef.current.disconnect();
        centrifugeRef.current = null;
        userSubRef.current = null;
        systemSubRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    let mounted = true;

    const connect = async () => {
      try {
        const tokenResponse = await api.getWebSocketToken();

        if (!mounted) return;

        const centrifuge = new Centrifuge(tokenResponse.url, {
          token: tokenResponse.token,
        });

        centrifuge.on('connected', () => {
          if (mounted) {
            setIsConnected(true);
            console.log('WebSocket connected');
          }
        });

        centrifuge.on('disconnected', () => {
          if (mounted) {
            setIsConnected(false);
            console.log('WebSocket disconnected');
          }
        });

        centrifuge.on('error', (ctx) => {
          console.error('WebSocket error:', ctx);
        });

        // Subscribe to user-specific channel (personal#user_id - enforced by Centrifugo)
        const userSub = centrifuge.newSubscription(tokenResponse.user_channel);
        userSub.on('publication', handlePublication);
        userSub.on('subscribed', () => {
          console.log(`Subscribed to user channel: ${tokenResponse.user_channel}`);
        });
        userSub.on('error', (ctx) => {
          console.error('User subscription error:', ctx);
        });
        userSub.subscribe();

        // Subscribe to system-wide broadcast channel
        const systemSub = centrifuge.newSubscription(tokenResponse.system_channel);
        systemSub.on('publication', handlePublication);
        systemSub.on('subscribed', () => {
          console.log(`Subscribed to system channel: ${tokenResponse.system_channel}`);
        });
        systemSub.on('error', (ctx) => {
          console.error('System subscription error:', ctx);
        });
        systemSub.subscribe();

        centrifuge.connect();

        centrifugeRef.current = centrifuge;
        userSubRef.current = userSub;
        systemSubRef.current = systemSub;
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
      }
    };

    connect();

    return () => {
      mounted = false;
      if (centrifugeRef.current) {
        centrifugeRef.current.disconnect();
        centrifugeRef.current = null;
        userSubRef.current = null;
        systemSubRef.current = null;
      }
    };
  }, [isAuthenticated, handlePublication]);

  const value: WebSocketContextType = {
    isConnected,
    subscribe,
    lastEvent,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextType {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}
